import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import db from "./Firebase";

const fmtTs = (val) => {
    if (!val) return "-";
    if (val?.toDate) return val.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    if (typeof val === "string") return val;
    return "-";
};

const deduplicateByPhoneWorkshop = (data) => {
    const seen = new Set();
    return data.filter(r => {
        const key = `${(r.phone_no || "").trim()}__${(r.workshop_name || "").trim()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export default function WorkshopJoinClick() {
    const navigate = useNavigate();

    useEffect(() => {
        const email = localStorage.getItem("email");
        const password = localStorage.getItem("password");
        const remember = localStorage.getItem("remember");
        if (remember === "false" || !email || !password) navigate("/signin");
    }, [navigate]);

    const [rows, setRows] = useState([]);
    const [totalRaw, setTotalRaw] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");

    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            const snap = await getDocs(collection(db, "nv_join_workshop_click"));
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setTotalRaw(data.length);
            const sorted = data.sort((a, b) => {
                const ta = a.created_date?.toDate?.()?.getTime() ?? 0;
                const tb = b.created_date?.toDate?.()?.getTime() ?? 0;
                return tb - ta;
            });
            setRows(deduplicateByPhoneWorkshop(sorted));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const filtered = rows.filter(r => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
            (r.name || "").toLowerCase().includes(q) ||
            (r.phone_no || "").toLowerCase().includes(q) ||
            (r.username || "").toLowerCase().includes(q) ||
            (r.workshop_name || "").toLowerCase().includes(q) ||
            (r.subscription_plan || "").toLowerCase().includes(q)
        );
    });

    const handleExport = () => {
        const exportRows = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name || "",
            "Phone": r.phone_no || "",
            "Username": r.username || "",
            "Workshop Name": r.workshop_name || "",
            "Subscription Plan": r.subscription_plan || "",
            "Subscription Start": fmtTs(r.subscription_start_date),
            "Subscription End": fmtTs(r.subscription_end_date),
            "Created Date": fmtTs(r.created_date),
        }));
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "WorkshopJoinClicks");
        saveAs(
            new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `workshop_join_clicks_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Workshop Join Clicks</h3>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, phone, username, workshop…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "300px" }}
                        />
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            {loading ? "Loading…" : "Refresh"}
                        </button>
                        <button
                            className="btn btn-success btn-sm"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted">Loading data…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Username</th>
                                    <th>Workshop Name</th>
                                    <th>Subscription Plan</th>
                                    <th>Sub Start</th>
                                    <th>Sub End</th>
                                    <th>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">
                                            No records found
                                        </td>
                                    </tr>
                                ) : filtered.map((r, i) => (
                                    <tr key={r.id}>
                                        <td>{i + 1}</td>
                                        <td>{r.name || "-"}</td>
                                        <td>{r.phone_no || "-"}</td>
                                        <td>{r.username || "-"}</td>
                                        <td>{r.workshop_name || "-"}</td>
                                        <td>{r.subscription_plan || "-"}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtTs(r.subscription_start_date)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtTs(r.subscription_end_date)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtTs(r.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer text-muted" style={{ fontSize: "12px" }}>
                        {filtered.length} unique records shown (total in DB: {totalRaw})
                    </div>
                </div>
            )}
        </div>
    );
}
